import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, At, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { mAuth } from "@/lib/api";
import { BlonixWordmark } from "@/components/BlonixLogo";

export default function MerchantLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("manager@demo.blonix.io");
  const [password, setPassword] = useState("Demo@Blonix2025");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await mAuth.login(email, password);
      toast.success("Welcome");
      navigate("/m");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="merchant-login-page">
      <div className="hidden lg:flex flex-1 bg-zinc-950 text-white p-14 flex-col justify-between">
        <div>
          <BlonixWordmark className="h-6 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Merchant Portal</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-3">Sandbox-first by design</div>
          <div className="font-display text-3xl leading-tight tracking-tight max-w-md">
            Issue cards. Move money. Test every edge case before you ship.
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-sm text-white/80">
            <Stat n="Sandbox" l="default mode" />
            <Stat n="HMAC" l="optional signing" />
            <Stat n="<5min" l="time to first card" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-8 lg:p-14 max-w-xl mx-auto w-full">
        <div className="lg:hidden mb-8">
          <BlonixWordmark className="h-6 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Merchant Portal</div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Sign in</div>
            <h1 className="font-display text-4xl tracking-tight font-medium text-zinc-950">Merchant Portal</h1>
            <p className="mt-3 text-sm text-zinc-500">Manage cards, API keys, and KYB for your account.</p>

            <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="m-login-form">
              <Input label="Email" testId="m-login-email" type="email" value={email} onChange={setEmail} icon={At} />
              <Input label="Password" testId="m-login-password" type="password" value={password} onChange={setPassword} icon={Lock} />
              <button
                type="submit"
                disabled={loading}
                data-testid="m-login-submit-btn"
                className="w-full h-10 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50"
              >
                {loading ? "Authenticating…" : (<>Continue <ArrowRight size={14} /></>)}
              </button>
              <div className="text-xs text-zinc-500 text-center pt-2">
                New here?{" "}
                <Link to="/m/signup" data-testid="goto-signup" className="text-zinc-950 underline hover:no-underline">Create a merchant account</Link>
              </div>
              <div className="pt-4 border-t border-zinc-100">
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">Demo credential</div>
                <div className="text-xs font-mono text-zinc-700">manager@demo.blonix.io · Demo@Blonix2025</div>
              </div>
            </form>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 mt-8">
          © {new Date().getFullYear()} Blonix Technologies Limited
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-xl font-medium tabular-nums">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.1em] text-white/60 mt-0.5">{l}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, icon: Icon }) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && showPassword ? "text" : type;

  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />}
        <input
          type={inputType} required
          data-testid={testId}
          value={value} onChange={(e) => onChange(e.target.value)}
          className={`w-full h-10 ${Icon ? "pl-9" : "pl-3"} ${isPassword ? "pr-9" : "pr-3"} text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none`}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
            data-testid={testId ? `${testId}-toggle` : undefined}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
          >
            {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}

export { Input };
